import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Mail, Phone, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { FAQ, FAQ_EN } from "@/lib/data";
import { STRINGS } from "@/lib/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Contact() {
  const { lang } = useLanguage();
  const t = STRINGS[lang].contact;
  const faq = lang === "en" ? FAQ_EN : FAQ;
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.success);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contato" className="mx-auto max-w-7xl px-5 py-28 sm:px-8 lg:py-36" data-testid="contact-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="mb-16 max-w-2xl"
      >
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400/80">{t.overline}</p>
        <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
          {t.title[0]}
          <em className="italic text-blue-400">{t.title[1]}</em>
        </h2>
      </motion.div>

      <div className="grid gap-16 lg:grid-cols-2">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="space-y-5 rounded-2xl border border-white/10 bg-[#0B101C]/80 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.4)] backdrop-blur-xl sm:p-9"
          data-testid="contact-form"
        >
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.name}</label>
            <input
              required
              value={form.name}
              onChange={set("name")}
              data-testid="contact-name-input"
              placeholder={t.namePh}
              className="w-full rounded-lg border border-white/10 bg-[#05070D] px-4 py-3 text-sm text-slate-100 outline-none transition-colors duration-300 placeholder:text-slate-600 focus:border-blue-500/50"
            />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.email}</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              data-testid="contact-email-input"
              placeholder={t.emailPh}
              className="w-full rounded-lg border border-white/10 bg-[#05070D] px-4 py-3 text-sm text-slate-100 outline-none transition-colors duration-300 placeholder:text-slate-600 focus:border-blue-500/50"
            />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.message}</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              data-testid="contact-message-input"
              placeholder={t.messagePh}
              className="w-full resize-none rounded-lg border border-white/10 bg-[#05070D] px-4 py-3 text-sm text-slate-100 outline-none transition-colors duration-300 placeholder:text-slate-600 focus:border-blue-500/50"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="flex items-center justify-center gap-3 rounded-full bg-blue-500 px-8 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-blue-400 disabled:opacity-60"
          >
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}
            {sending ? t.submitting : t.submit}
          </button>
        </motion.form>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="space-y-5 border-b border-white/5 pb-10">
            {[
              [Mail, "ricardogtr@icloud.com", "contact-info-email", null],
              [Phone, "+1 818-205-8456", "contact-info-phone", `https://wa.me/18182058456?text=${encodeURIComponent(lang === "en" ? "Hi Ricardo! I'd like to book a trial guitar lesson." : "Olá Ricardo! Quero agendar uma aula experimental de guitarra.")}`],
              [Clock, t.hours, "contact-info-hours", null],
            ].map(([Icon, text, testid, href]) => (
              <div key={testid} className="flex items-center gap-4" data-testid={testid}>
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-blue-500/25 bg-blue-500/5">
                  <Icon className="h-4 w-4 text-blue-400" />
                </span>
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${testid}-link`}
                    className="text-sm text-slate-300 underline-offset-4 transition-colors duration-300 hover:text-blue-300 hover:underline"
                  >
                    {text}
                  </a>
                ) : (
                  <p className="text-sm text-slate-300">{text}</p>
                )}
              </div>
            ))}
          </div>

          <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
            {faq.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/8">
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="text-left font-serif text-lg text-slate-200 hover:text-blue-300 hover:no-underline"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-400" data-testid={`faq-answer-${i}`}>
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
